import { useState } from "react";
import { Send, Sparkles, Loader2, BookOpen, FileText, Brain, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import HidroMarkdown from "../HidroMarkdown";

/**
 * Wave 24.3 — Ask the Library
 * Search bar at the top of the Library tab.  Hidro scans every resolved
 * engineering summary + datasheet metadata in one Claude call and returns
 * a cited answer.  Citations are rendered as clickable pills.
 */
export default function AskLibraryBar() {
  const aiName = "Hidro";
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async (e) => {
    e?.preventDefault?.();
    const question = q.trim();
    if (!question) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/engineering/library/ask",
        { question },
        { timeout: 120_000 },
      );
      setResult({ ...data, question });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${aiName} couldn't answer right now.`);
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setResult(null);
    setQ("");
  };

  return (
    <div className="space-y-3" data-testid="ask-library-bar">
      <form onSubmit={ask} className="flex items-stretch gap-2 hv-card p-2 border-[#1FB6C1]/30 bg-gradient-to-br from-[#1FB6C1]/5 to-transparent">
        <div className="flex items-center gap-2 px-2 text-[#1FB6C1]">
          <Brain className="w-5 h-5" />
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask the library — e.g. “How do I diagnose RO permeate flux drop on a 4040 element?”"
          className="hv-input flex-1 border-0 focus:ring-0 bg-transparent text-sm"
          data-testid="ask-library-input"
          disabled={busy}
          maxLength={600}
        />
        <button
          type="submit"
          disabled={busy || !q.trim()}
          className="hv-btn hv-btn-primary text-sm"
          data-testid="ask-library-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Thinking…" : `Ask ${aiName}`}
        </button>
      </form>
      {!result && !busy && (
        <div className="text-[11px] text-[#647087] px-1">
          {aiName} will scan every resolved session + datasheet in your library and answer with citations.
        </div>
      )}
      {result && <AskLibraryResult result={result} onClear={clear} />}
    </div>
  );
}

function AskLibraryResult({ result, onClear }) {
  const libRefs = result.sources?.library || [];
  const dsRefs = result.sources?.datasheets || [];
  // Wave 26.7 — preprocess the answer so [LIB:id] and [DS:id] tokens become
  // markdown links with custom hrefs. The custom `a` renderer below
  // detects those hrefs and swaps them for chip components — preserving
  // citation chips inline AND giving us full markdown formatting (bold,
  // italic, headings, lists, tables, code).
  const libMap = new Map(libRefs.map((r) => [r.id, r]));
  const dsMap = new Map(dsRefs.map((r) => [r.id, r]));
  const preprocessed = (result.answer || "").replace(
    /\[(LIB|DS):([^\]\s]+)\]/g,
    (_full, kind, id) => {
      const r = (kind === "LIB" ? libMap : dsMap).get(id);
      const label = r?.title ? r.title.slice(0, 40) : id;
      // Use a fake URL scheme so the anchor renderer can detect it.
      return `[${label}](hidro-cite:${kind}:${id})`;
    },
  );
  return (
    <div className="hv-card p-4 border-[#1FB6C1]/30" data-testid="ask-library-result">
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-8 h-8 rounded-md bg-gradient-to-br from-[#1FB6C1] to-[#0d6e75] flex items-center justify-center text-white">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-0.5">Hidro · Library answer</div>
          <div className="font-display text-sm font-bold truncate" title={result.question}>{result.question}</div>
          <div className="text-[10px] text-[#647087] mt-0.5">
            Drew from {result.corpus_size?.library || 0} sessions and {result.corpus_size?.datasheets || 0} datasheets.
          </div>
        </div>
        <button onClick={onClear} className="hv-btn hv-btn-ghost text-xs" data-testid="ask-library-clear">
          <X className="w-3 h-3" /> Clear
        </button>
      </div>
      <HidroMarkdown
        text={preprocessed}
        className="text-sm"
        testId="ask-library-answer"
        components={{
          a: ({ node, href, children, ...props }) => {
            const m = /^hidro-cite:(LIB|DS):(.+)$/.exec(href || "");
            if (!m) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1FB6C1] hover:underline"
                  {...props}
                >
                  {children}
                </a>
              );
            }
            const [, kind, id] = m;
            if (kind === "LIB") {
              return (
                <Link
                  to={`/tools/engineering-desk/library/${id}`}
                  className="inline-flex items-center align-baseline mx-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30 hover:bg-[#1FB6C1]/25 transition no-underline"
                  data-testid={`ask-library-cite-LIB-${id}`}
                >
                  <BookOpen className="w-2.5 h-2.5 mr-0.5" />
                  {children}
                </Link>
              );
            }
            return (
              <span
                className="inline-flex items-center align-baseline mx-0.5 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#A78BFA]/15 text-[#A78BFA] border border-[#A78BFA]/30"
                data-testid={`ask-library-cite-DS-${id}`}
              >
                <FileText className="w-2.5 h-2.5 mr-0.5" />
                {children}
              </span>
            );
          },
        }}
      />
      {(libRefs.length > 0 || dsRefs.length > 0) && (
        <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
          {libRefs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-1.5 inline-flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" /> Library sources
              </div>
              <div className="flex flex-wrap gap-1.5">
                {libRefs.map((r) => (
                  <Link
                    key={r.id}
                    to={`/tools/engineering-desk/library/${r.id}`}
                    className="text-[11px] px-2 py-0.5 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/30 hover:bg-[#1FB6C1]/20 transition"
                    data-testid={`ask-library-libref-${r.id}`}
                  >
                    {r.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {dsRefs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-1.5 inline-flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Datasheet sources
              </div>
              <div className="flex flex-wrap gap-1.5">
                {dsRefs.map((r) => (
                  <span
                    key={r.id}
                    className="text-[11px] px-2 py-0.5 rounded bg-[#A78BFA]/10 text-[#A78BFA] border border-[#A78BFA]/30"
                    data-testid={`ask-library-dsref-${r.id}`}
                  >
                    {r.manufacturer ? `${r.manufacturer} · ` : ""}{r.title}{r.part_number ? ` (${r.part_number})` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function renderInlineCitations(_text, _libRefs, _dsRefs) {
  // Deprecated — kept as a no-op shim in case a stale import lingers; the
  // live render path now uses HidroMarkdown's `components.a` override.
  return null;
}
